import { cn, focusRing, Skeleton } from "@simpro/ui";
import { useQuery } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { openBranch } from "@/lib/branch";
import { ProblemCard } from "@/shared/components/ProblemCard";
import { publicBranchesQuery } from "../api/branches.queries";
import type { PublicBranch } from "../schemas/branches.schemas";

export interface BranchPickerProps {
  /** The salon's name, for the heading. */
  salonName: string;
  /** Why the picker is shown instead of a branch's page (FR-SA-248). */
  reason?: "choose" | "closed" | "unknown";
}

const HEADINGS = {
  choose: { title: "Choose your branch", note: "Prices, stylists and times are each branch's own." },
  closed: { title: "This branch has closed", note: "Choose another branch to book." },
  unknown: { title: "We could not find that branch", note: "The link may be old. Choose a branch to book." },
} as const;

/**
 * The salon's open branches, grouped by city (FR-SA-244, FR-SA-245, FR-SA-248; design note
 * salon-multi-branch.md 9.4). Shown for a bare link at a salon with several branches, for an old link
 * to a branch that has closed, and for "Change branch". Choosing one opens its page, which this phone
 * then remembers, and moves only between this salon's branches: never to another salon.
 */
export function BranchPicker({ salonName, reason = "choose" }: BranchPickerProps) {
  const branches = useQuery(publicBranchesQuery());
  const heading = HEADINGS[reason];
  const cities = groupByCity(branches.data ?? []);

  return (
    <div className="mx-auto flex w-full max-w-2xl animate-arrive flex-col gap-8 px-4 pt-10 pb-20 md:px-8 md:pt-16">
      <div className="flex flex-col gap-3 px-1">
        {salonName ? <p className="eyebrow text-accent-ink">{salonName}</p> : null}
        <h1 className="display-section text-heading">{heading.title}</h1>
        <p className="text-[15px] leading-relaxed text-subtle">{heading.note}</p>
      </div>

      {branches.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading the branches">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
        </div>
      ) : branches.isError ? (
        <ProblemCard title="We could not load the branches" description="Check your connection and try again." onRetry={() => void branches.refetch()} />
      ) : (
        cities.map(([city, inCity]) => (
          <section key={city} aria-labelledby={`city-${city}`} className="flex flex-col gap-3">
            <h2 id={`city-${city}`} className="eyebrow text-subtle">
              {city}
            </h2>
            <ul className="flex flex-col gap-3">
              {inCity.map((branch) => (
                <li key={branch.slug}>
                  <button
                    type="button"
                    onClick={() => openBranch(branch.slug)}
                    className={cn(
                      "flex w-full items-start gap-4 rounded-2xl border border-edge bg-surface p-5 text-left transition-colors hover:bg-overlay",
                      focusRing,
                    )}
                  >
                    <MapPin className="mt-0.5 size-5 shrink-0 text-accent-ink" aria-hidden="true" />
                    <span className="flex flex-col gap-1">
                      <span className="headline text-[19px] text-heading">{branch.name}</span>
                      <span className="text-sm text-subtle">{branch.addressLine1}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

/** Cities in the order their first branch opened, each with its branches in the same order. */
function groupByCity(branches: PublicBranch[]): [string, PublicBranch[]][] {
  const cities = new Map<string, PublicBranch[]>();
  for (const branch of branches) {
    cities.set(branch.city, [...(cities.get(branch.city) ?? []), branch]);
  }
  return [...cities.entries()];
}
